import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

const LawFirmSearch = ({
  value,
  onChange,
  onBlur,
  onSelect,
  results,
  loading,
  inputClassName = 'form-control',
  placeholder = 'Search law firm name',
}) => {
  const [showResults, setShowResults] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const searchResultsRef = useRef(null)
  const inputRef = useRef(null)

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Reset highlighted index when results change
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [results])

  const handleInputChange = e => {
    onChange(e)
    setShowResults(true)
  }

  const handleResultClick = lawFirm => {
    onSelect(lawFirm)
    setShowResults(false)
  }

  const handleKeyDown = e => {
    // Handle arrow navigation for search results
    if (showResults && results.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1))
      } else if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault()
        handleResultClick(results[highlightedIndex])
      } else if (e.key === 'Escape') {
        setShowResults(false)
      }
    }
  }

  return (
    <div className='law-firm-search position-relative'>
      <div className='input-group'>
        <input
          ref={inputRef}
          type='text'
          className={inputClassName}
          value={value}
          onChange={handleInputChange}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setShowResults(true)}
          placeholder={placeholder}
          autoComplete='off'
        />
        {loading && (
          <div
            className='position-absolute'
            style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
          >
            <div className='spinner-border spinner-border-sm text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div
          ref={searchResultsRef}
          className='search-results position-absolute w-100 bg-white border rounded-bottom shadow-sm mt-1'
          style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}
        >
          {results.map((result, index) => (
            <div
              key={result.id}
              className={`search-result p-2 cursor-pointer ${highlightedIndex === index ? 'bg-light' : ''}`}
              onClick={() => handleResultClick(result)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {result.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

LawFirmSearch.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  results: PropTypes.array,
  loading: PropTypes.bool,
  inputClassName: PropTypes.string,
  placeholder: PropTypes.string,
}

export default LawFirmSearch
